import React from 'react';
import { motion } from 'framer-motion';
import {
  IoSearch,
  IoAddCircleOutline,
  IoArrowUp,
} from 'react-icons/io5';
const PatientsListView = ({
  patients,
  loading,
  patientSearch,
  setPatientSearch,
  orderTypeFilter,
  setOrderTypeFilter,
  ivrStatusFilter,
  setIvrStatusFilter,
  careTypeFilter,
  setCareTypeFilter,
  deliveryStatusFilter,
  setDeliveryStatusFilter,
  patientNameSortOrder,
  togglePatientNameSort,
  sortPatientsByName,
  setSelectedPatientDetail,
  setCurrentView,
  setOrderStep,
  fastOrders = [],
  loadingFastOrders = false,
}) => {
  // Helper function to get IVR status display from backend
  const getIvrStatusDisplay = (patient) => {
    const ivrStatusDisplay = patient.latest_ivr_status_display;
    const ivrStatus = patient.latest_ivr_status;
    
    if (!ivrStatusDisplay && !ivrStatus) {
      return { text: "N/A", className: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300" };
    }
    
    const statusToCheck = (ivrStatusDisplay || ivrStatus || "").toLowerCase();
    
    if (statusToCheck === "no ivr submitted" || statusToCheck === "") {
      return { text: "N/A", className: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300" };
    }
    
    if (statusToCheck.includes("approved") || statusToCheck === "approved") {
      return { text: "Completed", className: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300" };
    }
    
    if (statusToCheck.includes("pending") || statusToCheck.includes("submitted") || statusToCheck === "pending" || statusToCheck === "submitted") {
      return { text: "Pending", className: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300" };
    }
    
    if (statusToCheck.includes("withdrawn") || statusToCheck === "withdrawn") {
      return { text: "Withdrawn", className: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300" };
    }
    
    return { text: ivrStatusDisplay || ivrStatus || "N/A", className: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300" };
  };

  // Helper function to get delivery status
  const getDeliveryStatus = (patient) => {
    if (!patient.latest_order_status) return 'Pending';
    const status = patient.latest_order_status.toLowerCase();
    if (status === 'delivered') return 'Delivered';
    if (status === 'shipped' || status === 'active') return 'In Transit';
    if (status === 'pending' || status === 'approved') return 'Pending';
    return 'Pending';
  };

  // Filter patients based on search and all filters
  const filteredPatients = patients.filter(p => {
    // Search filter
    if (patientSearch) {
      const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
      if (!fullName.includes(patientSearch.toLowerCase())) {
        return false;
      }
    }

    // Only apply these filters for Guided Orders
    if (orderTypeFilter === 'guided') {
      // IVR Status filter
      if (ivrStatusFilter) {
        const ivrStatus = getIvrStatusDisplay(p);
        if (ivrStatus.text !== ivrStatusFilter) {
          return false;
        }
      }

      // Care Type filter
      if (careTypeFilter) {
        const careType = p.conservative_care ? 'Conservative' : 'Advanced';
        if (careType !== careTypeFilter) {
          return false;
        }
      }

      // Delivery Status filter
      if (deliveryStatusFilter) {
        const deliveryStatus = getDeliveryStatus(p);
        if (deliveryStatus !== deliveryStatusFilter) {
          return false;
        }
      }
    }

    return true;
  });

  const sortedAndFilteredPatients = sortPatientsByName(filteredPatients);

  return (
    <motion.div 
      className="w-full bg-gray-50 dark:bg-gray-900 pb-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-[95%] xl:max-w-[98%] mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">All Patients</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage and view all patient records
              </p>
            </div>
            
            {/* Patient Count Badge */}
            {!loading && (
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-900 dark:text-white">{filteredPatients.length}</span> of <span className="font-semibold text-gray-900 dark:text-white">{patients.length}</span> patients
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Search Bar */}
          <motion.div 
            className="mb-6"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="relative max-w-lg">
              <IoSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl z-10" />
              <input
                type="text"
                placeholder="Search by patient name..."
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium placeholder:text-gray-400 shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
              />
            </div>
          </motion.div>

          {/* Order Type Filter */}
          <motion.div 
            className="mb-6"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.12 }}
          >
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Order Type:
              </label>
              <select
                value={orderTypeFilter}
                onChange={(e) => {
                  setOrderTypeFilter(e.target.value);
                  // Reset other filters when switching
                  setIvrStatusFilter('');
                  setCareTypeFilter('');
                  setDeliveryStatusFilter('');
                }}
                className="px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-semibold shadow-sm hover:border-blue-400 dark:hover:border-blue-600"
              >
                <option value="guided">Propack Guided Orders (IVR included)</option>
                <option value="fast">Fast Orders (Wholesale)</option>
              </select>
            </div>
          </motion.div>

          {/* Conditional Filters - Only show for Guided Orders */}
          {orderTypeFilter === 'guided' && (
            <motion.div 
              className="mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15 }}
            >
              <div className="flex flex-wrap gap-4">
                {/* IVR Status Filter */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IVR Status
                  </label>
                  <select
                    value={ivrStatusFilter}
                    onChange={(e) => setIvrStatusFilter(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
                  >
                    <option value="">All</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                    <option value="Withdrawn">Withdrawn</option>
                    <option value="N/A">N/A</option>
                  </select>
                </div>

                {/* Care Type Filter */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Care Type
                  </label>
                  <select
                    value={careTypeFilter}
                    onChange={(e) => setCareTypeFilter(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
                  >
                    <option value="">All</option>
                    <option value="Advanced">Advanced Care</option>
                    <option value="Conservative">Conservative Care</option>
                  </select>
                </div>

                {/* Delivery Status Filter */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Delivery Status
                  </label>
                  <select
                    value={deliveryStatusFilter}
                    onChange={(e) => setDeliveryStatusFilter(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
                  >
                    <option value="">All</option>
                    <option value="Delivered">Delivered</option>
                    <option value="In Transit">In Transit</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Conditional Table Rendering */}
        {loading && patients.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="relative mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto"></div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-semibold text-lg">Loading patients...</p>
            </motion.div>
          </div>
        ) : filteredPatients.length === 0 ? (
          <motion.div 
            className="text-center py-24 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="text-7xl mb-6">
              {orderTypeFilter === 'fast' ? '🚀' : '👥'}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {patientSearch ? 'No patients found' : orderTypeFilter === 'fast' ? 'No fast orders yet' : 'No patients yet'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
              {patientSearch 
                ? 'Try adjusting your search terms' 
                : orderTypeFilter === 'fast'
                ? 'Create your first fast order'
                : 'Create your first order to add patients'}
            </p>
            {!patientSearch && (
              <motion.button 
                onClick={() => {
                  setCurrentView('orderModeSelection');
                  setOrderStep(1);
                }}
                className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoAddCircleOutline className="text-xl" />
                Create First Order
              </motion.button>
            )}
          </motion.div>
        ) : orderTypeFilter === 'guided' ? (
          // GUIDED ORDERS TABLE
          <motion.div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-b-2 border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      <button
                        onClick={togglePatientNameSort}
                        title={patientNameSortOrder === 'asc' ? 'Sort descending by first name' : 'Sort ascending by first name'}
                        className="flex items-center gap-2 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                      >
                        Patient Name
                        <motion.div
                          animate={{ rotate: patientNameSortOrder === 'desc' ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <IoArrowUp className="text-base" />
                        </motion.div>
                      </button>
                    </th>
                    {/* <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Date of Birth
                    </th> */}
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      IVR Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Care Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Delivery Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedAndFilteredPatients.map((patient, index) => {
                    const careType = patient.conservative_care ? 'Conservative' : 'Advanced';
                    const deliveryStatus = getDeliveryStatus(patient);
                    const ivrStatus = getIvrStatusDisplay(patient);
                    
                    return (
                      <motion.tr 
                        key={patient.id}
                        className={`${
                          index % 2 === 0 
                            ? 'bg-white dark:bg-gray-800' 
                            : 'bg-gray-50 dark:bg-gray-700/20'
                        } hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-all duration-150 border-b border-gray-100 dark:border-gray-700/50`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 dark:from-teal-500 dark:to-teal-700 flex items-center justify-center text-white font-bold text-sm mr-4 shadow-md">
                              {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <button
                                onClick={() => setSelectedPatientDetail(patient)}
                                className="text-base font-semibold text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors text-left block truncate"
                              >
                                {patient.first_name} {patient.last_name}
                              </button>
                            </div>
                          </div>
                        </td>
                        {/* <td className="px-6 py-5 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {patient.date_of_birth 
                              ? new Date(patient.date_of_birth).toLocaleDateString('en-US', { 
                                  month: '2-digit', 
                                  day: '2-digit', 
                                  year: 'numeric' 
                                })
                              : <span className="text-gray-400">N/A</span>}
                          </div>
                        </td> */}
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${ivrStatus.className}`}>
                            <span className="w-2 h-2 rounded-full bg-current opacity-60 mr-2"></span>
                            {ivrStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                            careType === 'Conservative' 
                              ? 'bg-gradient-to-r from-purple-100 to-purple-200 dark:from-purple-900/40 dark:to-purple-800/40 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-700'
                              : 'bg-gradient-to-r from-indigo-100 to-indigo-200 dark:from-indigo-900/40 dark:to-indigo-800/40 text-indigo-800 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-700'
                          }`}>
                            <span className="w-2 h-2 rounded-full bg-current opacity-60 mr-2"></span>
                            {careType}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                            deliveryStatus === 'Delivered' 
                              ? 'bg-gradient-to-r from-green-100 to-green-200 dark:from-green-900/40 dark:to-green-800/40 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
                              : deliveryStatus === 'In Transit'
                              ? 'bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700'
                              : 'bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-800/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-700'
                          }`}>
                            <span className="w-2 h-2 rounded-full bg-current opacity-60 mr-2"></span>
                            {deliveryStatus}
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
  // FAST ORDERS TABLE
  <motion.div 
    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
  >
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-b-2 border-orange-200 dark:border-orange-700">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Order ID
            </th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Patient Name/ID
            </th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Product SKU
            </th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Treatment Type
            </th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Shipping Status
            </th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Admin Call Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {loadingFastOrders ? (
            <tr>
              <td colSpan="6" className="px-8 py-12 text-center">
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
                </div>
                <p className="text-gray-500 dark:text-gray-400 mt-4 font-semibold">
                  Loading fast orders...
                </p>
              </td>
            </tr>
          ) : fastOrders.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-8 py-12 text-center">
                <div className="text-6xl mb-4">🚀</div>
                <p className="text-gray-500 dark:text-gray-400 font-semibold text-lg mb-2">
                  No fast orders yet
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  Create your first fast order to see it here
                </p>
              </td>
            </tr>
          ) : (
            fastOrders.map((order, index) => (
              <motion.tr
                key={order.id}
                className={`${
                  index % 2 === 0 
                    ? 'bg-white dark:bg-gray-800' 
                    : 'bg-orange-50/30 dark:bg-orange-900/10'
                } hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-150 border-b border-orange-100 dark:border-orange-900/30`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className="text-sm font-bold text-gray-900 dark:text-white bg-orange-100 dark:bg-orange-900/30 px-3 py-1 rounded-md">
                    {order.order_number}
                  </span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {order.patient_name_or_id}
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {order.product_sku}
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      ({order.size} × {order.quantity})
                    </span>
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                    order.treatment_type === 'POST_OP'
                      ? 'bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/40 dark:to-cyan-900/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700'
                      : 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/40 dark:to-pink-900/40 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-700'
                  }`}>
                    <span className="w-2 h-2 rounded-full bg-current opacity-60 mr-2"></span>
                    {order.treatment_type_display || order.treatment_type}
                  </span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                    order.status === 'delivered'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
                      : order.status === 'shipped'
                      ? 'bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/40 dark:to-cyan-900/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700'
                      : 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-700'
                  }`}>
                    <span className="w-2 h-2 rounded-full bg-current opacity-60 mr-2"></span>
                    {order.status_display}
                  </span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                    order.admin_call_complete
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
                      : 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600'
                  }`}>
                    <span className="w-2 h-2 rounded-full bg-current opacity-60 mr-2"></span>
                    {order.admin_call_complete ? 'Complete' : 'Pending'}
                  </span>
                </td>
              </motion.tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </motion.div>
)}
      </div>
    </motion.div>
  );
};

export default PatientsListView;